import React from 'react';
import { clearAllTimers, clearTimer, setTimer } from './FieldInteractionsHelper';

interface UseFieldStateReturn {
  dynamicChoices: Record<string, string[]>;
  loadingFields: Set<string>;
  searchTimers: React.RefObject<Record<string, NodeJS.Timeout>>;
  enabledFields: Set<string>;
  setDynamicChoices: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  addLoadingField: (fieldName: string) => void;
  removeLoadingField: (fieldName: string) => void;
  clearFieldChoices: (fieldName: string) => void;
  setFieldChoices: (fieldName: string, choices: string[]) => void;
  clearSearchTimer: (fieldName: string) => void;
  setSearchTimer: (fieldName: string, timer: NodeJS.Timeout) => void;
  clearAllSearchTimers: () => void;
  addEnabledField: (fieldName: string) => void;
  removeEnabledField: (fieldName: string) => void;
}

export const useFieldState = (): UseFieldStateReturn => {
  const [dynamicChoices, setDynamicChoices] = React.useState<Record<string, string[]>>({});
  const [loadingFields, setLoadingFields] = React.useState<Set<string>>(new Set());
  const [enabledFields, setEnabledFields] = React.useState<Set<string>>(new Set());
  const searchTimers = React.useRef<Record<string, NodeJS.Timeout>>({});

  const addLoadingField = (fieldName: string) => {
    setLoadingFields(prev => new Set(prev).add(fieldName));
  };

  const removeLoadingField = (fieldName: string) => {
    setLoadingFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(fieldName);
      return newSet;
    });
  };

  const addEnabledField = (fieldName: string) => {
    setEnabledFields(prev => new Set(prev).add(fieldName));
  };

  const removeEnabledField = (fieldName: string) => {
    setEnabledFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(fieldName);
      return newSet;
    });
  };

  const clearFieldChoices = (fieldName: string) => {
    setDynamicChoices(prev => ({ ...prev, [fieldName]: [] }));
  };

  const setFieldChoices = (fieldName: string, choices: string[]) => {
    setDynamicChoices(prev => ({ ...prev, [fieldName]: choices }));
  };

  const clearSearchTimer = (fieldName: string) => {
    clearTimer(fieldName, searchTimers);
  };

  const setSearchTimer = (fieldName: string, timer: NodeJS.Timeout) => {
    setTimer(fieldName, timer, searchTimers);
  };

  const clearAllSearchTimers = () => {
    clearAllTimers(searchTimers);
  };

  React.useEffect(() => {
    return () => {
      clearAllTimers(searchTimers);
    };
  }, []);

  return {
    dynamicChoices,
    loadingFields,
    searchTimers,
    enabledFields,
    setDynamicChoices,
    addLoadingField,
    removeLoadingField,
    clearFieldChoices,
    setFieldChoices,
    clearSearchTimer,
    setSearchTimer,
    clearAllSearchTimers,
    addEnabledField,
    removeEnabledField
  };
};
